import React, { useContext } from 'react';
import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { useSnackbar } from 'notistack';
import CustomSelect from '../../../../Components/Inputs/CustomSelect';
import DepositPaidAmountDialog from '../../../../Components/DepositPaidAmountDialog';
import {
	BID_STATUS_COLORS,
	BID_STATUS_LABELS,
	useBidStatusChange,
	useBidStatusOptions,
} from '../../../../Shared/bids';
import { JobDetailContext } from '../../context/JobDetailContext';
import { makeApiRequest } from '../../../../Shared/Api';
import useJobStatuses from '../../../../Hooks/useJobStatuses';

const StatusCard = ({ onJobUpdated }) => {
	const { state, dispatch } = useContext(JobDetailContext);
	const { enqueueSnackbar } = useSnackbar();
	const { selectedJob } = state;
	const { labels, colors, options } = useJobStatuses();
	const bidDetails = selectedJob?.bid_details;
	const bidStatusOptions = useBidStatusOptions(bidDetails);
	const { changeBidStatus, depositPrompt, closeDepositPrompt, confirmDialog } =
		useBidStatusChange({ onJobUpdated });

	if (!selectedJob) {
		return null;
	}

	const handleStatusChange = async (value, type) => {
		if (type === 'job_status') {
			const previousStatus = selectedJob.job_status;
			dispatch({
				type: 'SET_JOB',
				payload: { ...selectedJob, job_status: value },
			});
			try {
				await makeApiRequest('PATCH', `/jobs/${selectedJob.id}/`, {
					job_status: value,
				});
			} catch (err) {
				dispatch({
					type: 'SET_JOB',
					payload: { ...selectedJob, job_status: previousStatus },
				});
				enqueueSnackbar(err.message || 'Could not update job status.', {
					variant: 'error',
				});
			}
		} else if (type === 'bid_status' && bidDetails) {
			await changeBidStatus(value);
		}
	};

	const jobStatusLabel =
		labels[selectedJob.job_status] || selectedJob.job_status;
	const jobStatusColor = colors[selectedJob.job_status] || 'default';

	return (
		<>
			<Card elevation={0}>
				<CardContent sx={{ p: 2.5 }}>
					<Box>
						<Typography
							variant="caption"
							sx={{
								display: 'block',
								mb: 1,
								fontWeight: 600,
								color: 'text.secondary',
								textTransform: 'uppercase',
								fontSize: '0.65rem',
								letterSpacing: 0.5,
							}}
						>
							Job Status
						</Typography>
						<CustomSelect
							value={selectedJob.job_status}
							items={options}
							valueField="value"
							labelField="label"
							onChange={(e) => {
								handleStatusChange(e.target.value, 'job_status');
							}}
							sx={{
								'& .MuiOutlinedInput-root': {
									'& fieldset': { border: 'none' },
									'&:hover fieldset': { border: 'none' },
									'&.Mui-focused fieldset': { border: 'none' },
								},
								'& .MuiSelect-select': {
									p: '0 !important',
									display: 'flex',
									alignItems: 'center',
								},
								'& .MuiSelect-icon': {
									display: 'none',
								},
							}}
							renderValue={() => (
								<Chip
									label={jobStatusLabel}
									color={jobStatusColor}
									sx={{
										cursor: 'pointer',
										width: '100%',
										height: 'auto',
										py: 1.5,
										fontSize: '0.875rem',
										fontWeight: 600,
										'& .MuiChip-label': {
											px: 1,
										},
									}}
								/>
							)}
						/>
					</Box>

					{bidDetails ? (
						<Box sx={{ mt: 2.5 }}>
							<Typography
								variant="caption"
								sx={{
									display: 'block',
									mb: 1,
									fontWeight: 600,
									color: 'text.secondary',
									textTransform: 'uppercase',
									fontSize: '0.65rem',
									letterSpacing: 0.5,
								}}
							>
								Bid Status
							</Typography>
							<CustomSelect
								value={bidDetails.status}
								items={bidStatusOptions}
								valueField="value"
								labelField="label"
								onChange={(e) => {
									handleStatusChange(e.target.value, 'bid_status');
								}}
								sx={{
									'& .MuiOutlinedInput-root': {
										'& fieldset': { border: 'none' },
										'&:hover fieldset': { border: 'none' },
										'&.Mui-focused fieldset': { border: 'none' },
									},
									'& .MuiSelect-select': {
										p: '0 !important',
										display: 'flex',
										alignItems: 'center',
									},
									'& .MuiSelect-icon': {
										display: 'none',
									},
								}}
								renderValue={(selected) => (
									<Chip
										label={BID_STATUS_LABELS[selected]}
										color={BID_STATUS_COLORS[selected]}
										sx={{
											cursor: 'pointer',
											width: '100%',
											height: 'auto',
											py: 1.5,
											fontSize: '0.875rem',
											fontWeight: 600,
											'& .MuiChip-label': {
												px: 1,
											},
										}}
									/>
								)}
							/>
						</Box>
					) : null}
				</CardContent>
			</Card>

			{confirmDialog}
			<DepositPaidAmountDialog
				open={Boolean(depositPrompt)}
				defaultAmount={depositPrompt?.defaultAmount}
				onConfirm={(amount) => closeDepositPrompt(amount)}
				onCancel={() => closeDepositPrompt(null)}
			/>
		</>
	);
};

export default StatusCard;
